import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";

const resetSchema = z.object({
  email: z.string().trim().email({ message: "有効なメールアドレスを入力してください" }),
});

type ResetForm = z.infer<typeof resetSchema>;

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<ResetForm>({
    resolver: zodResolver(resetSchema),
    defaultValues: { email: "" },
  });

  const onSubmit = async (values: ResetForm) => {
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "エラー",
        description: error.message,
      });
      return;
    }

    setSent(true);
  };

  // Handle the recovery callback (user clicked the email link)
  const [newPassword, setNewPassword] = useState("");
  const [updating, setUpdating] = useState(false);
  const hash = window.location.hash;
  const isRecovery = hash.includes("type=recovery");

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast({ variant: "destructive", title: "エラー", description: "パスワードは6文字以上で入力してください" });
      return;
    }
    setUpdating(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setUpdating(false);

    if (error) {
      toast({ variant: "destructive", title: "エラー", description: error.message });
      return;
    }

    toast({ title: "パスワードを更新しました" });
    navigate("/", { replace: true });
  };

  if (isRecovery) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-full max-w-sm px-6">
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold tracking-wider text-foreground">SATTOU.</h1>
          </div>
          <form onSubmit={handleUpdatePassword} className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">新しいパスワード</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="h-11"
                autoComplete="new-password"
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full h-11" disabled={updating}>
              {updating ? "更新中..." : "パスワードを更新"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-full max-w-sm px-6 text-center space-y-4">
          <h1 className="text-4xl font-bold tracking-wider text-foreground">SATTOU.</h1>
          <p className="text-foreground">パスワードリセットのメールを送信しました。</p>
          <p className="text-sm text-muted-foreground">メール内のリンクからパスワードを再設定してください。</p>
          <Button variant="outline" onClick={() => navigate("/login")} className="mt-4">
            ログインに戻る
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-full max-w-sm px-6">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold tracking-wider text-foreground">SATTOU.</h1>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>メールアドレス</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" autoComplete="email" className="h-11" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full h-11 text-base" disabled={loading}>
              {loading ? "送信中..." : "リセットメールを送信"}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-sm text-primary hover:underline"
              >
                ログインに戻る
              </button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default ResetPassword;
